<script lang="ts">
  import { formatEntry, parseValue, printShelves, shelvesModel, swiftArray } from '../../lib/labs';
  import { sessions } from './lab-session.svelte';

  /**
   * 値型のコピー（labs.md ラボ 3 手順 4、components.md §3.2「コピーして操作」）。
   * 配列 a を var b = a で写すと棚が 2 つに分かれ、片方だけを操作しても、もう片方は変わらない。
   */
  const session = sessions.shelves;
  let valueText = $state('4');
  let status = $state('');

  const st = $derived(session.state);

  function say(t: string) {
    status = t;
  }

  function append(target: 'a' | 'b') {
    const v = parseValue(valueText);
    if (v === null) {
      say('値に -999〜999 の整数を入れてください。');
      return;
    }
    say(session.apply({ type: 'append', target, value: v }).message);
    if (v < 999) valueText = String(v + 1);
  }
</script>

<section class="panel shelves" aria-labelledby="shelves-title" data-testid="lab-copy-shelves">
  <h2 id="shelves-title">写す: <code>var b = a</code> の後の 2 つの棚</h2>
  <p class="small">配列 <code>a</code> を <code>b</code> に写してから、片方だけを操作します。</p>
  <div class="cols">
    <div class="pair">
      {#each ['a', 'b'] as const as name (name)}
        {@const items = name === 'a' ? st.a : st.b}
        <div class="shelf" class:split={st.b !== null}>
          <p class="shelf-name">{name}</p>
          {#if items === null}
            <p class="text-soft small">まだありません</p>
          {:else}
            <ol class="arr" aria-label="{name} の中身">
              {#each items as v, i (i)}<li>{v}</li>{/each}
              {#if items.length === 0}<li class="empty">空です</li>{/if}
            </ol>
            <div class="row">
              <button type="button" class="btn btn-sm" onclick={() => append(name)}
                >{name}.append</button
              >
              <button
                type="button"
                class="btn btn-sm"
                onclick={() => say(session.apply({ type: 'removeLast', target: name }).message)}
                >{name}.removeLast</button
              >
            </div>
          {/if}
        </div>
      {/each}
    </div>
    <div class="side">
      <div class="row">
        <div class="field val">
          <label for="shelf-val">値</label>
          <input id="shelf-val" type="text" inputmode="numeric" bind:value={valueText} />
        </div>
        <button
          type="button"
          class="btn btn-primary"
          onclick={() => say(session.apply({ type: 'copy' }).message)}
          disabled={st.b !== null}
          data-testid="shelves-copy">var b = a</button
        >
        <button
          type="button"
          class="btn btn-quiet"
          onclick={() => {
            session.reset();
            say('最初からにしました。');
          }}>最初から</button
        >
      </div>
      <pre class="code small-code" aria-label="写すラボの記録">{[
          ...shelvesModel.declarations(session.run.initial),
          ...session.entries.map(formatEntry),
          `print(a${st.b !== null ? ', b' : ''})  // ${printShelves(st)}`,
        ].join('\n')}</pre>
      <p class="status-line" role="status">{status}</p>
      {#if st.b !== null && swiftArray(st.a) !== swiftArray(st.b)}
        <p class="small">
          <code>a</code> と <code>b</code> は別の棚です。辞書も <code>var b = dict</code> で同じように別になります。
        </p>
      {/if}
    </div>
  </div>
</section>

<style>
  .shelves {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-top: var(--space-5);
  }
  .shelves h2 {
    margin: 0;
  }
  .cols {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  .pair {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-3);
  }
  .shelf {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    background: var(--canvas);
    border-radius: var(--radius-md);
    transition: transform var(--dur-slow) var(--ease-out);
  }
  .shelf.split {
    animation: split var(--dur-slow) var(--ease-out);
  }
  @keyframes split {
    from {
      transform: translateX(-1rem);
    }
    to {
      transform: none;
    }
  }
  :global(html[data-motion='reduce']) .shelf.split {
    animation: none;
  }
  .shelf-name {
    margin: 0;
    font-family: var(--font-mono);
    font-weight: var(--fw-bold);
  }
  .arr {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
    min-height: 2.4rem;
  }
  .arr li {
    min-width: 2.2rem;
    padding: var(--space-1);
    border: 2px solid var(--border-strong);
    border-radius: var(--radius-sm);
    background: var(--surface);
    text-align: center;
    font-family: var(--font-mono);
    font-weight: var(--fw-bold);
  }
  .arr li.empty {
    border: 0;
    background: none;
    color: var(--text-soft);
    font-family: var(--font-sans);
    font-weight: var(--fw-regular);
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: flex-end;
  }
  .side {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .val {
    width: 5rem;
  }
  .val input {
    font-family: var(--font-mono);
    text-align: center;
  }
  .small-code {
    margin: 0;
    padding: var(--space-3);
    font-size: var(--fs-sm);
    overflow-x: auto;
  }
  .small {
    font-size: var(--fs-sm);
  }
  .status-line {
    min-height: 1.6em;
    margin: 0;
    font-size: var(--fs-sm);
  }
  @media (max-width: 1023px) {
    .cols {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
